<template>
  <div class="eui-o-pdfviewer" style="overflow: hidden; height: inherit">
    <div>
      <header class="eui-o-pdfviewer__header">
        <div>
          <PDFZoom :scale="scale"
                   @change="updateScale"
                   @fit="updateFit"
                   class="header-item"/>
          <Divider vertical secondary/>
          <Button v-if="preview" iconOnly size="large" @click.prevent.stop="togglePreview" color="primary">
            <Icon name="expand_more"/>
          </Button>
          <template v-if="pagination">
            <span class="eui-u-mx-2" :class="{ 'eui-u-ml-4': !preview }">Page</span>
            <Input class="eui-u-w-16" type="number" min="1" v-model="handlerPagination" :max="pageCount" @keypress.enter.prevent/>
            <span class="eui-u-mx-2">/ <strong>{{ pageCount }}</strong></span>
          </template>
        </div>
        <div class="eui-o-pdfviewer__title" :title="title">
          <Heading class="eui-o-pdfviewer__titleContent" :level="2">{{ title }}</Heading>
        </div>
        <div>
          <slot name="header"></slot>
        </div>
        <div v-if="headerActions" class="eui-i-pdfviewer__header-right">
          <Button iconOnly size="large" @click="shareDocument" color="primary">
            <Icon name="share" />
          </Button>
          <Button iconOnly size="large" @click="downloadDocument" color="primary">
            <Icon name="download" />
          </Button>
          <Button iconOnly size="large" @click="printDocument" color="primary">
            <Icon name="print" />
          </Button>
        </div>
      </header>
      <div class="eui-o-pdfviewer__container">
        <PDFData class="eui-o-pdfviewer__main"
                 ref="eui-o-pdfData"
                 :url="pdf"
                 :base64="base64"
                 @page-count="updatePageCount"
                 @page-focus="updateCurrentPage"
                 @document-rendered="onDocumentRendered"
                 @document-errored="onDocumentErrored"
                 :buffer="buffer">
          <Divider secondary/>
          <template v-if="preview" v-slot:preview="{pages}">
            <PDFPreview
                ref="pdfPreview"
                v-show="isPreviewEnabled"
                class="pdf-viewer__preview"
                v-bind="{pages, scale, currentPage, pageCount, isPreviewEnabled}"
                @page-focus="onPageClick"
            />
          </template>
          <template v-slot:document="{pages}">
            <PDFDocument v-bind="{pages, scale, optimalScale, fit, currentPage, pageCount, isPreviewEnabled}"
                         :singlePage="singlePage"
                         @dropZone="$emit('dropZone', $event)"
                         @scale-change="updateScale"
                         @page-click="onPageClick">
              <template v-for="page in pages" v-slot:[`zone-${page.pageNumber}`]>
                <slot :name="`zone-${page.pageNumber}`"/>
              </template>
            </PDFDocument>
          </template>
        </PDFData>
      </div>
    </div>
  </div>
</template>

<script>
import PDFData from './_internal/PDFData';
import PDFDocument from './_internal/PDFDocument';
import PDFPreview from './_internal/PDFPreview';
import PDFZoom from './_internal/PDFZoom';
import Icon from '../../atoms/Icon/Icon';
import Input from '../../atoms/Input/Input';
import Button from '../../atoms/Button/Button';
import Divider from '../../atoms/Divider/Divider';
import Heading from '../../atoms/Heading/Heading';

function floor(value, precision) {
  const multiplier = Math.pow(10, precision || 0);
  return Math.floor(value * multiplier) / multiplier;
}

export default {
  name: 'PDFViewer',

  components: {
    PDFData,
    PDFDocument,
    PDFPreview,
    PDFZoom,
    Icon,
    Button,
    Input,
    Divider,
    Heading
  },

  data() {
    return {
      scale: 1,
      optimalScale: undefined,
      fit: undefined,
      pageCount: undefined,
      isPreviewEnabled: false,
      handlerPagination: undefined,
    };
  },

  props: {
    pagination: {
      type: Boolean,
      default: false,
    },
    pdf: {
      required: true,
    },
    title: String,
    singlePage: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    preview: {
      type: Boolean,
      default: false,
    },
    headerActions: {
      type: Boolean,
      default: false,
    },
    base64: {
      type: Boolean,
      default: false,
    },
    buffer: {
      type: Boolean,
      default: true,
    }
  },

  methods: {
    onDocumentRendered($event) {
      this.$emit('document-rendered', $event);
    },

    onDocumentErrored(e) {
      this.$emit('document-errored', e);
    },

    updateScale({scale, isOptimal = false}) {
      const roundedScale = floor(scale, 2);
      if (isOptimal) this.optimalScale = roundedScale;
      this.scale = roundedScale;
      this.$emit('scale-change', this.scale);
    },

    updateFit(fit) {
      this.fit = fit;
    },

    updatePageCount(pageCount) {
      this.pageCount = pageCount;
      this.$emit('page-count', pageCount);
    },

    updateCurrentPage(pageNumber) {
      this.$emit('update:currentPage', pageNumber);
    },

    togglePreview() {
      this.isPreviewEnabled = !this.isPreviewEnabled;
    },

    onPageClick(payload) {
      this.$emit('page-click', payload);
    },

    shareDocument() {
      // eslint-disable-next-line no-console
      console.log('share: ', this.title); //todo: share function
    },
    downloadDocument() {
      // eslint-disable-next-line no-console
      console.log('download: ', this.title); //todo: download function
    },
    printDocument() {
      // eslint-disable-next-line no-console
      console.log('title: ', this.title); //todo: print function
    }
  },

  watch: {
    currentPage(value) {
      this.pageNumber = value;
      this.handlerPagination = value;
      if (this.preview) this.$refs.pdfPreview.$refs[`scrollingDocument-${this.$refs.pdfPreview._uid}`].$refs[`pdfPreview-${value}-${this.$refs.pdfPreview.$refs[`scrollingDocument-${this.$refs.pdfPreview._uid}`]._uid}`][0].$el.scrollIntoView();
    },
    handlerPagination(oldValue) {
      if (Number(oldValue) > 1 && Number(oldValue) <= this.pageCount) this.updateCurrentPage(Number(this.handlerPagination));
      else this.updateCurrentPage(1);
    },
  },

  mounted() {
    this.handlerPagination = this.currentPage;
    this.pageNumber = this.currentPage;
    document.body.classList.add('overflow-hidden');
  },
};
</script>
